/**
 * The family-filtered button rail (KHIYARSA-EDITOR-1 Phase 4, §B1).
 *
 * Buttons are a shortcut over an ordinary text document, never a structured
 * model behind it: every one of them produces text the user could have typed,
 * and typing is never blocked or reformatted. The set on offer comes from
 * `buttonsFor`, which reads the compiler's own vocabulary, so the rail cannot
 * advertise something the family rejects.
 */
import React from "react";

import { FAMILIES } from "../vocab";

import { buttonsFor, starterFor } from "./insert";
import { useEditorT } from "./strings";

import type { ActionId } from "./insert";
import type { Family } from "../types";

export interface KmdButtonRailProps {
  /** null until the document declares a family the compiler understands */
  family: Family | null;
  onAction: (action: ActionId) => void;
  onNewDiagram: (starter: string) => void;
}

export const KmdButtonRail = ({
  family,
  onAction,
  onNewDiagram,
}: KmdButtonRailProps) => {
  const t = useEditorT();

  // No frontmatter yet — the only useful thing to offer is a starting point,
  // which is also the fix for KMD004 ("no type; defaulted to flow")
  if (!family) {
    return (
      <div className="kmd-editor__rail kmd-editor__rail--chooser">
        <span className="kmd-editor__rail-label">{t("newDiagram")}</span>
        {FAMILIES.map((candidate) => (
          <button
            key={candidate}
            className="kmd-editor__chip"
            onClick={() => onNewDiagram(starterFor(candidate))}
          >
            {t(`family_${candidate}` as never)}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="kmd-editor__rail">
      {buttonsFor(family).map((button) => (
        <button
          key={button.id}
          className="kmd-editor__chip"
          onClick={() => onAction(button.id)}
          title={t(button.labelKey as never)}
        >
          {t(button.labelKey as never)}
        </button>
      ))}
    </div>
  );
};
